import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { defer, Subject, switchMap, tap } from 'rxjs';

import {
	AUTH_SERVER_URL, CLIENT_ID, REDIRECT_URI, AUTH_REQUIRED_URLS, OLD_STEELUSER_URL
} from '../../config';
import { User } from '../models/user';
import { uuidv4 } from '../utils/uuid';

import { AUTH_GET_USER_DATA_URL, LOGGING_OUT } from './connection.common';

interface State {
	uuid: string;
}

interface TokenResponse {
	access_token: string;
	token_type: string;
	expires_in: number;
	refresh_token?: string;
	refresh_token_expires_in?: number;
}

interface AuthorizationCodeParams {
	grantType: 'authorizationCode';
	clientId: string;
	code: string;
	scope: string;
	redirectUri: string;
	codeVerifier: string;
}

interface RefreshTokenParams {
	grantType: 'refreshToken';
	clientId: string;
	refreshToken: string;
}

const OAUTH2_CURRENT_ATTEMPT_WAS_ERROR = 'copo-oauth2-current-attempt-was-error';
const OAUTH2_LAST_ATTEMPT_WAS_ERROR = 'copo-oauth2-last-attempt-was-error';

async function requestToken(tokenParams: AuthorizationCodeParams | RefreshTokenParams) {
	return new Promise<TokenStore>((resolve, reject) => {
		const tokenXhr = new XMLHttpRequest();
		tokenXhr.open('POST', `${ AUTH_SERVER_URL }/auth/token`, true);
		tokenXhr.responseType = 'json';
		tokenXhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
		tokenXhr.addEventListener(
			'load',
			() => {
				if (tokenXhr.status !== 200) {
					reject(new Error(`bad http status: ${ tokenXhr.status }`));
					return;
				}
				let response = <TokenResponse | string> tokenXhr.response;
				if (typeof response === 'string') {
					response = <TokenResponse> JSON.parse(response);
				}
				if (response.token_type.toLocaleLowerCase() !== 'bearer') {
					reject(new Error(`unknown token type ${ response.token_type }`));
					return;
				}
				const date = tokenXhr.getResponseHeader('Date');
				let now;
				if (date) {
					now = new Date(date).getTime();
					if (Number.isNaN(now)) {
						now = Date.now();
					}
				} else {
					now = Date.now();
				}
				resolve({
					accessToken: response.access_token,
					expirationDate: now + (response.expires_in * 1000),
					refreshToken: response.refresh_token,
					refreshTokenExpirationDate: response.refresh_token_expires_in
						? now + (response.refresh_token_expires_in * 1000)
						: undefined,
				});
			},
			false
		);
		tokenXhr.addEventListener('error', reject, false);
		const params = new URLSearchParams();
		params.set('client_id', tokenParams.clientId);
		if (tokenParams.grantType === 'refreshToken') {
			params.set('grant_type', 'refresh_token');
			params.set('refresh_token', tokenParams.refreshToken);
		} else {
			params.set('grant_type', 'authorization_code');
			params.set('code', tokenParams.code);
			params.set('scope', tokenParams.scope);
			params.set('redirect_uri', tokenParams.redirectUri);
			params.set('code_verifier', tokenParams.codeVerifier);
		}
		tokenXhr.send(params);
	}).then(v => {
		sessionStorage.removeItem(OAUTH2_CURRENT_ATTEMPT_WAS_ERROR);
		sessionStorage.removeItem(OAUTH2_LAST_ATTEMPT_WAS_ERROR);
		return v;
	}).catch((error: unknown) => {
		const current = sessionStorage.getItem(OAUTH2_CURRENT_ATTEMPT_WAS_ERROR);
		if (current) {
			sessionStorage.setItem(OAUTH2_LAST_ATTEMPT_WAS_ERROR, current);
		}
		sessionStorage.setItem(OAUTH2_CURRENT_ATTEMPT_WAS_ERROR, 'true');
		throw error;
	});
}

function revokeToken(token: string, tokenTypeHint: string) {
	const tokenXhr = new XMLHttpRequest();
	tokenXhr.open('POST', `${ AUTH_SERVER_URL }/auth/invalidate_token`, true);
	tokenXhr.responseType = 'json';
	tokenXhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');

	const params = new URLSearchParams();
	params.set('token', token);
	params.set('token_type_hint', tokenTypeHint);
	params.set('client_id', CLIENT_ID);
	tokenXhr.send(params);
}

interface TokenStore {
	accessToken: string;
	expirationDate: number;
	refreshToken?: string | undefined;
	refreshTokenExpirationDate?: number | undefined;
}

const ACCESS_TOKEN = 'copo-access-token';
const OAUTH2_REQUEST_UUID = 'copo-oauth-request-uuid';
const OAUTH2_REQUEST_PATH = 'copo-oauth-request-path';
const OAUTH2_REQUEST_CODE_VERIFIER = 'copo-oauth-request-code-verifier';
const SELECTED_USER = 'copo-selected-user';
const SELECTED_VISIBILITY = 'copo-selected-visibility';

function getToken() {
	const tokenStore = localStorage.getItem(ACCESS_TOKEN);
	return tokenStore ? <TokenStore> JSON.parse(tokenStore) : null;
}

function dec2hex(dec: number) {
	return `0${ dec.toString(16) }`.slice(-2);
}

function generateRandomString() {
	const array = new Uint32Array(56 / 2);
	crypto.getRandomValues(array);
	return Array.from(array, dec2hex).join('');
}

async function generateCodeChallenge(codeVerifier: string) {
	const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));

	return btoa(String.fromCodePoint(...new Uint8Array(digest)))
		.replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
}

type ConnectedTask = (user: User) => Promise<void>;

type DisconnectedTask = () => Promise<void>;

type TokenObtainedTask = (path: string, lastRequestedPath?: string) => Promise<void>;

type TokenDestroyedTask = () => Promise<void>;

type ForbiddenAccessRefreshTask = () => void;

@Injectable({ providedIn: 'root' })
export class ConnectionService implements OnDestroy {
	checkingConnection = false;
	tokenLoading = false;
	awaitUserData = false;
	windowDisplayed = false;
	language = 'en-GB';
	/**
	 * when login page is opened, this keep the path to use as redirect
	 * (the last one that have been explicitly requested)
	 */
	lastRequestedPath?: string;

	tokenObtainedTask: TokenObtainedTask | undefined;
	tokenDestroyedTask: TokenDestroyedTask | undefined;
	forbiddenAccessRefreshTask: ForbiddenAccessRefreshTask | undefined;
	readonly connectedTasks: ConnectedTask[] = [];
	readonly disconnectedTasks: DisconnectedTask[] = [];

	private accessToken: TokenStore | null;
	private accessTokenPromise: Promise<string | null> | undefined;
	private readonly forbiddenAccessSubject = new Subject<boolean>();

	constructor(private readonly router: Router) {
		this.storeSelectedVisibilityParams();

		// migrate old token setting, to remove on next release
		const oldToken = localStorage.getItem('access_token');
		if (oldToken) {
			const newToken: TokenStore = {
				accessToken: oldToken,
				expirationDate: Date.now() + (1000 * 60 * 60 * 24 * 30)
			};
			localStorage.setItem(ACCESS_TOKEN, JSON.stringify(newToken));
			localStorage.removeItem('access_token');
		}
		this.accessToken = getToken();
	}

	ngOnDestroy() {
		this.tokenObtainedTask = undefined;
		this.tokenDestroyedTask = undefined;
		this.forbiddenAccessRefreshTask = undefined;
		this.connectedTasks.splice(0, this.connectedTasks.length);
		this.disconnectedTasks.splice(0, this.disconnectedTasks.length);
	}

	async emitConnected(user: User) {
		sessionStorage.removeItem(LOGGING_OUT);
		return Promise.all(this.connectedTasks.map(async task => task(user)));
	}

	async emitDisconnected() {
		await Promise.all(this.disconnectedTasks.map(async task => task()));
		const selectedUser = sessionStorage.getItem(SELECTED_USER);
		const selectedVisibility = sessionStorage.getItem(SELECTED_VISIBILITY);
		sessionStorage.clear();
		if (selectedUser !== null) {
			sessionStorage.setItem(SELECTED_USER, selectedUser);
		}
		if (selectedVisibility !== null) {
			sessionStorage.setItem(SELECTED_VISIBILITY, selectedVisibility);
		}
		sessionStorage.setItem(LOGGING_OUT, 'true');
	}

	isNotReadyToDisplayLogin() {
		return !navigator.onLine
			|| this.windowDisplayed
			|| this.tokenLoading
			|| this.awaitUserData
			|| this.checkingConnection;
	}

	async requestLogin(requestedPath?: string) {
		if (requestedPath) {
			this.lastRequestedPath = requestedPath;
		}

		if (this.isNotReadyToDisplayLogin()) {
			return;
		}
		this.checkingConnection = true;
		try {
			await this.checkConnection();
		} finally {
			this.checkingConnection = false;
		}

		const authUrl = new URL(`${ AUTH_SERVER_URL }/auth/oauth2`);
		const paramsAuth = authUrl.searchParams;
		const selectedVisibility = this.getSelectedVisibilityParams();
		const path = selectedVisibility
			? '/'
			: requestedPath
				|| (this.router.isActive(
					'/redirect',
					{ paths: 'subset', queryParams: 'subset', fragment: 'ignored', matrixParams: 'ignored' }
				)
					? '/'
					: this.router.url);
		paramsAuth.set('state', this.createState(path));
		paramsAuth.set('redirect_uri', REDIRECT_URI);
		paramsAuth.set('client_id', CLIENT_ID);
		paramsAuth.set('response_type', 'code');
		this.tokenLoading = true;
		const codeChallenge = await this.createCodeChallenge();
		paramsAuth.set('code_challenge_method', 'S256');
		paramsAuth.set('code_challenge', codeChallenge);
		// prevent redirect loop on error
		const lastAttemptWasError = sessionStorage.getItem(OAUTH2_LAST_ATTEMPT_WAS_ERROR);
		if (lastAttemptWasError) {
			paramsAuth.set('prompt', 'consent');
		}
		this.addSelectedVisibilityParams(paramsAuth, selectedVisibility);
		location.replace(authUrl.toString());
	}

	async createCodeChallenge() {
		const codeVerifier = generateRandomString();
		sessionStorage.setItem(OAUTH2_REQUEST_CODE_VERIFIER, codeVerifier);
		return generateCodeChallenge(codeVerifier);
	}

	getCodeVerifier() {
		const codeVerifier = sessionStorage.getItem(OAUTH2_REQUEST_CODE_VERIFIER);
		if (!codeVerifier) {
			throw new Error('no code verifier');
		}
		sessionStorage.removeItem(OAUTH2_REQUEST_CODE_VERIFIER);
		return codeVerifier;
	}

	createState(path: string) {
		const uuid = uuidv4();
		const state: State = { uuid };
		sessionStorage.setItem(OAUTH2_REQUEST_UUID, uuid);
		sessionStorage.setItem(OAUTH2_REQUEST_PATH, path || '/');
		// eslint-disable-next-line @typescript-eslint/no-deprecated
		return btoa(unescape(encodeURIComponent(JSON.stringify(state))));
	}

	parseState(state: string) {
		// eslint-disable-next-line @typescript-eslint/no-deprecated
		const stateObj = <State> JSON.parse(decodeURIComponent(escape(atob(state))));
		const storedUuid = sessionStorage.getItem(OAUTH2_REQUEST_UUID);
		if (!storedUuid || stateObj.uuid !== storedUuid) {
			throw new Error(
				`security error, state uuid mismatch: '${
					stateObj.uuid
				}' (received) '${
					String(storedUuid)
				}' (stored)`
			);
		}
		sessionStorage.removeItem(OAUTH2_REQUEST_UUID);
		const path = sessionStorage.getItem(OAUTH2_REQUEST_PATH) || '/';
		sessionStorage.removeItem(OAUTH2_REQUEST_PATH);
		return path;
	}

	getDownloadAccessToken() {
		if (!this.accessToken) {
			return 'unauthorized';
		}
		if (Date.now() > this.accessToken.expirationDate) {
			this.resetTokenData();
			return 'unauthorized';
		}
		return this.accessToken.accessToken;
	}

	hasValidAccessToken() {
		if (!this.accessToken) {
			return false;
		}
		const now = Date.now();
		if (now > this.accessToken.expirationDate) {
			return this.accessToken.refreshToken !== undefined
				&& (
					!this.accessToken.refreshTokenExpirationDate
					|| now < this.accessToken.refreshTokenExpirationDate
				);
		}
		return true;
	}

	getForbiddenAccessSubject() {
		return this.forbiddenAccessSubject;
	}

	async getAccessToken() {
		if (this.accessTokenPromise) {
			try {
				return await this.accessTokenPromise;
			} catch (err) {
				console.error('error trying to refresh token', err);
				this.resetTokenData();
				return null;
			}
		}
		try {
			this.accessTokenPromise = this.getAsyncAccessToken();
			return await this.accessTokenPromise;
		} finally {
			this.accessTokenPromise = undefined;
		}
	}

	async getAsyncAccessToken() {
		if (this.getSelectedVisibilityParams()) {
			this.resetTokenData();
			return null;
		}
		if (!this.accessToken) {
			return null;
		}
		const now = Date.now();
		if (now > this.accessToken.expirationDate) {
			if (
				this.accessToken.refreshToken !== undefined
				&& (
					!this.accessToken.refreshTokenExpirationDate
					|| now < this.accessToken.refreshTokenExpirationDate
				)
			) {
				try {
					const newTokenStore = await requestToken({
						grantType: 'refreshToken',
						clientId: CLIENT_ID,
						refreshToken: this.accessToken.refreshToken,
					});
					this.accessToken = {
						...this.accessToken,
						...newTokenStore
					};
					localStorage.setItem(ACCESS_TOKEN, JSON.stringify(this.accessToken));
					return this.accessToken.accessToken;
				} catch (err) {
					console.error('error trying to refresh token', err);
					this.resetTokenData();
					return null;
				}
			}
			this.resetTokenData();
			return null;
		}
		return this.accessToken.accessToken;
	}

	updateAccessToken() {
		this.accessToken = getToken();
	}

	async requestLogout(
		globalLogout = true,
		visibility?: { visibilityId?: number | undefined; userId: number; readOnly?: boolean | undefined }
	) {
		this.windowDisplayed = true;
		try {
			this.lastRequestedPath = '/';

			const token = this.accessToken;

			// remove the token for the current app
			this.resetTokenData();

			if (token) {
				// invalidate tokens on Auth if possible (internet)
				revokeToken(token.accessToken, 'access_token');
				if (token.refreshToken) {
					revokeToken(token.refreshToken, 'refresh_token');
				}
			}

			if (globalLogout) {
				// logout from old steeluser (continue if error)
				try {
					await new Promise((resolve, reject) => {
						const oldStuLogoutXhr = new XMLHttpRequest();
						oldStuLogoutXhr.open(
							'GET',
							`${ OLD_STEELUSER_URL }/homepage/managehomepage/logout.action?logout=true&json=true`,
							true
						);
						oldStuLogoutXhr.responseType = 'text';
						oldStuLogoutXhr.withCredentials = true;
						oldStuLogoutXhr.addEventListener(
							'load',
							() => {
								if (oldStuLogoutXhr.status !== 200) {
									reject(new Error(`bad http status: ${ oldStuLogoutXhr.status }`));
									return;
								}
								resolve(<string> oldStuLogoutXhr.response);
							},
							false
						);
						oldStuLogoutXhr.addEventListener('error', reject, false);
						oldStuLogoutXhr.send();
					});
				} catch (err) {
					console.error(err);
				}
			}
		} finally {
			this.windowDisplayed = false;
		}

		// clear local storage for user
		if (this.tokenDestroyedTask) {
			this.tokenLoading = true;
			try {
				await this.tokenDestroyedTask();
			} finally {
				this.tokenLoading = false;
			}
		}

		if (this.isNotReadyToDisplayLogin()) {
			return;
		}
		this.checkingConnection = true;
		try {
			await this.checkConnection();
		} finally {
			this.checkingConnection = false;
		}

		// logout from auth server
		const authUrl = new URL(`${ AUTH_SERVER_URL }/auth/oauth2`);
		const paramsAuth = authUrl.searchParams;
		paramsAuth.set('state', this.createState('/'));
		paramsAuth.set('redirect_uri', REDIRECT_URI);
		paramsAuth.set('client_id', CLIENT_ID);
		paramsAuth.set('response_type', 'code');
		this.tokenLoading = true;
		const codeChallenge = await this.createCodeChallenge();
		paramsAuth.set('code_challenge_method', 'S256');
		paramsAuth.set('code_challenge', codeChallenge);
		// prevent redirect loop on error
		const lastAttemptWasError = sessionStorage.getItem(OAUTH2_LAST_ATTEMPT_WAS_ERROR);
		if (lastAttemptWasError) {
			paramsAuth.set('prompt', 'consent');
		}

		if (globalLogout) {
			const logoutUrl = new URL(`${ AUTH_SERVER_URL }/auth/logout`);
			const paramsCallback = logoutUrl.searchParams;
			paramsCallback.set('callback', authUrl.toString());

			location.replace(logoutUrl.toString());
		} else {
			if (visibility) {
				paramsAuth.set('selected_user', String(visibility.userId));
				if (visibility.visibilityId) {
					paramsAuth.set('selected_visibility', String(visibility.visibilityId));
				}
				if (visibility.readOnly !== undefined) {
					paramsAuth.set('read_only', String(visibility.readOnly));
				}
			}

			location.replace(authUrl.toString());
		}
	}

	async handleCode(code: string, state: string) {
		this.tokenLoading = true;
		try {
			const path = this.parseState(state);
			const codeVerifier = this.getCodeVerifier();
			this.accessToken = await requestToken({
				grantType: 'authorizationCode',
				clientId: CLIENT_ID,
				code,
				scope: 'user',
				redirectUri: REDIRECT_URI,
				codeVerifier
			});
			localStorage.setItem(ACCESS_TOKEN, JSON.stringify(this.accessToken));
			// clear user database if exists
			if (this.tokenObtainedTask) {
				await this.tokenObtainedTask(path, this.lastRequestedPath);
			}
		} finally {
			this.tokenLoading = false;
			if (!this.accessToken) {
				await this.requestLogin('/');
			}
		}
	}

	resetTokenData() {
		this.accessToken = null;
		localStorage.removeItem(ACCESS_TOKEN);
	}

	private getSelectedVisibilityParams() {
		const currentUrl = new URL(location.href);
		const routeParams = new URLSearchParams(currentUrl.hash.split('?')[1] ?? '');
		const routerParams = new URLSearchParams(this.router.url.split('?')[1] ?? '');
		const selectedUser = currentUrl.searchParams.get('selected_user')
			?? routeParams.get('selected_user')
			?? routerParams.get('selected_user')
			?? sessionStorage.getItem(SELECTED_USER);
		const selectedVisibility = currentUrl.searchParams.get('selected_visibility')
			?? routeParams.get('selected_visibility')
			?? routerParams.get('selected_visibility')
			?? sessionStorage.getItem(SELECTED_VISIBILITY);
		return selectedUser !== null || selectedVisibility !== null ? { selectedUser, selectedVisibility } : null;
	}

	private storeSelectedVisibilityParams() {
		const currentUrl = new URL(location.href);
		const routeParams = new URLSearchParams(currentUrl.hash.split('?')[1] ?? '');
		const selectedUser = currentUrl.searchParams.get('selected_user')
			?? routeParams.get('selected_user');
		const selectedVisibility = currentUrl.searchParams.get('selected_visibility')
			?? routeParams.get('selected_visibility');
		if (selectedUser !== null) {
			sessionStorage.setItem(SELECTED_USER, selectedUser);
		}
		if (selectedVisibility !== null) {
			sessionStorage.setItem(SELECTED_VISIBILITY, selectedVisibility);
		}
	}

	private clearSelectedVisibilityParams() {
		sessionStorage.removeItem(SELECTED_USER);
		sessionStorage.removeItem(SELECTED_VISIBILITY);
	}

	private addSelectedVisibilityParams(
		paramsAuth: URLSearchParams,
		selectedVisibility: { selectedUser: string | null; selectedVisibility: string | null } | null
	) {
		if (selectedVisibility?.selectedUser !== null && selectedVisibility?.selectedUser !== undefined) {
			paramsAuth.set('selected_user', selectedVisibility.selectedUser);
		}
		if (selectedVisibility?.selectedVisibility !== null && selectedVisibility?.selectedVisibility !== undefined) {
			paramsAuth.set('selected_visibility', selectedVisibility.selectedVisibility);
		}
		if (selectedVisibility) {
			this.clearSelectedVisibilityParams();
		}
	}

	private async checkConnection() {
		return new Promise<string>((resolve, reject) => {
			const checkConnectionXhr = new XMLHttpRequest();
			checkConnectionXhr.open('GET', `${ AUTH_SERVER_URL }/auth/ok`, true);
			checkConnectionXhr.responseType = 'text';
			checkConnectionXhr.addEventListener(
				'load',
				() => {
					if (checkConnectionXhr.status !== 200) {
						reject(new Error(`bad http status: ${ checkConnectionXhr.status }`));
						return;
					}
					if (checkConnectionXhr.response !== 'OK') {
						reject(new Error(`bad response: ${ String(checkConnectionXhr.response) }`));
						return;
					}
					resolve(<string> checkConnectionXhr.response);
				},
				false
			);
			checkConnectionXhr.addEventListener('error', reject, false);
			checkConnectionXhr.send();
		});
	}
}

@Injectable({ providedIn: 'root' })
export class ConnectionInterceptor implements HttpInterceptor {
	constructor(
		private readonly connectionService: ConnectionService
	) {}

	intercept(req: HttpRequest<unknown>, next: HttpHandler) {
		let requireAuth = false;
		for (const authRequiredUrl of AUTH_REQUIRED_URLS) {
			if (req.url.startsWith(authRequiredUrl)) {
				requireAuth = true;
				break;
			}
		}
		if (!requireAuth) {
			return next.handle(req);
		}

		return defer(async () => {
			const token = await this.connectionService.getAccessToken();
			if (!token) {
				this.connectionService.requestLogin().catch((err: unknown) => {
					console.error('error while request login', err);
				});
				throw new Error('token is missing');
			}
			return req.headers
				.set('Accept-Language', this.connectionService.language)
				.set('Authorization', `Bearer ${ token }`);
		}).pipe(
			switchMap(headers => next.handle(req.clone({ headers }))),

			tap({
				error: (event: unknown) => {
					if (event instanceof HttpErrorResponse && event.status === 403) {
						this.connectionService.getForbiddenAccessSubject().next(true);
						if (
							!event.url?.includes(AUTH_GET_USER_DATA_URL)
							&& this.connectionService.forbiddenAccessRefreshTask
						) {
							this.connectionService.forbiddenAccessRefreshTask();
						}
					}
					if (!(event instanceof HttpErrorResponse) || event.status !== 401) {
						return;
					}
					const wwwAuthHeader = event.headers.get('WWW-Authenticate');
					if (!wwwAuthHeader?.startsWith('Bearer error="invalid_token"')) {
						return;
					}
					this.connectionService.resetTokenData();
					this.connectionService.requestLogin().catch((err: unknown) => {
						console.error('error while request login', err);
					});
				}
			})
		);
	}
}
